import type { AuthnMethod, FactorOption } from '@imprivata-cloud/authn';
import { debug } from '@imprivata-cloud/common';
import { useMemo } from 'react';

import { QueryParam } from '@/constants';
import { getUrlParamRaw, hasDisableAutoComplete } from '@/utils';

export const useQueryParams = () => {
  // Read on every render, not via useLocation: in EAM, useAgent refreshes
  // rpRequestId/PKCE with history.replaceState, which the router never sees.
  // The re-render from setShowUI must pick the new values up.
  const search = window.location.search;

  const queryParams = useMemo(() => new URLSearchParams(search), [search]);

  const applicationId = queryParams.get(QueryParam.ApplicationId) ?? undefined;
  const authnWorkflowId =
    queryParams.get(QueryParam.AuthnWorkflowId) ?? undefined;
  const clientName = queryParams.get(QueryParam.ClientName) ?? undefined;
  const tenantId = queryParams.get(QueryParam.TenantId) ?? '';
  const rpRequestId = queryParams.get(QueryParam.RpRequestId) ?? '';
  const hideFactorSwitch = !queryParams.get(QueryParam.ShowAddMethod);
  // Raw-decoded (not URLSearchParams) so a literal '+' in the value (e.g. an
  // email-style username) survives instead of being read as a space.
  const loginHint = getUrlParamRaw(QueryParam.LoginHint) ?? null;

  const contextResource = useMemo(() => {
    if (applicationId) {
      return { resourceType: '', contextType: '' };
    }
    return {
      resourceType: queryParams.get(QueryParam.ResourceType) ?? '',
      contextType: queryParams.get(QueryParam.ContextType) ?? '',
    };
  }, [applicationId, queryParams]);

  const oidcRequestData = useMemo(
    () => ({
      codeChallenge: queryParams.get(QueryParam.CodeChallenge) ?? '',
      codeChallengeMethod:
        queryParams.get(QueryParam.CodeChallengeMethod) ?? '',
    }),
    [queryParams],
  );

  const initialFactors = useMemo(() => {
    const factors = queryParams.get(QueryParam.AcrValues);
    if (Array.isArray(factors)) {
      return factors.map(factor => ({
        factorType: factor as AuthnMethod,
      })) as FactorOption[];
    }
    return factors ? ([{ factorType: factors }] as FactorOption[]) : undefined;
  }, [queryParams]);

  const fitVideoFrameString = queryParams.get(QueryParam.FitVideoFrame);
  const fitVideoFrame = fitVideoFrameString
    ? fitVideoFrameString === 'true'
    : undefined;

  const disableAutoComplete = hasDisableAutoComplete(search);

  const acrValues = queryParams.get(QueryParam.AcrValues);
  const isOneToMany = !loginHint && acrValues === 'face';

  debug('[QUERY-PARAMS]', {
    initialFactors,
    oidcRequestData,
    applicationId,
    authnWorkflowId,
    clientName,
    tenantId,
    rpRequestId,
    hideFactorSwitch,
    contextResource,
    fitVideoFrame,
    loginHint,
    isOneToMany,
    disableAutoComplete,
  });
  return {
    initialFactors,
    oidcRequestData,
    applicationId,
    authnWorkflowId,
    clientName,
    tenantId,
    rpRequestId,
    hideFactorSwitch,
    contextResource,
    fitVideoFrame,
    loginHint,
    isOneToMany,
    disableAutoComplete,
  };
};
